import { styled, SxProps, Theme } from "@mui/material";
import { useCountdown } from "../../services/CountDown";
import { Text } from "../Typography";

const DateTimeDisplay = ({ small, medium, value, type }: any) => {
  return (
    <TimerItem className="timerItem_over">
      <Text large={!small && !medium} small={small && !medium} medium={medium} numeric>
        {value}
      </Text>
      <Text medium={!medium && !small} small={medium || small} numeric>
        {type}
      </Text>
    </TimerItem>
  );
};

const CountdownTimer = ({
  targetDate,
  sx,
  small,
  medium,
  triggerExpired,
}: {
  sx?: SxProps<Theme>;
  targetDate: Date | null;
  yearsShow?: boolean;
  hideSeconds?: boolean;
  hideMinutes?: boolean;
  small?: boolean;
  medium?: boolean;
  triggerExpired?: () => void;
}) => {
  const [years, months, days, hours, minutes, seconds] = useCountdown(targetDate);

  if (!targetDate) {
    return null;
  }
  if (targetDate.getTime() < Date.now()) {
    if (triggerExpired) {
      triggerExpired();
    }
    // Same shell as a running unit — TimerItem/`timerItem_over` + a `Text` span, with the size
    // props DateTimeDisplay picks. Call sites style the digits through `& .timerItem_over span`
    // (see LandPage's auction bar), so a bare text node here fell outside those overrides and
    // took the page font: the mono/uppercase countdown became plain sans "Expired" mid-row.
    return (
      <Timer sx={sx} className={"expiredTimer"}>
        <TimerItem className="timerItem_over">
          <Text large={!small && !medium} small={small && !medium} medium={medium} numeric>
            Expired
          </Text>
        </TimerItem>
      </Timer>
    );
  } else {
    const num = {
      Y: Number(years),
      M: Number(months),
      D: Number(days),
      h: Number(hours),
      m: Number(minutes),
      s: Number(seconds),
    };

    let showYears = false;
    let showMonths = false;
    let showDays = false;
    let showHours = false;
    let showMinutes = false;
    let showSeconds = false;

    if (num.Y > 0) {
      showYears = true;
      showMonths = true;
      showDays = true;
    } else if (num.M > 0) {
      showMonths = true;
      showDays = true;
      showHours = true;
    } else if (num.D > 0) {
      showDays = true;
      showHours = true;
      showMinutes = true;
    } else if (num.h > 0) {
      showHours = true;
      showMinutes = true;
      showSeconds = true;
    } else {
      showMinutes = true;
      showSeconds = true;
    }

    return (
      <Timer sx={sx}>
        {showYears && <DateTimeDisplay small={small} medium={medium} value={years} type={"Y"} />}
        {showMonths && <DateTimeDisplay small={small} medium={medium} value={months} type={"M"} />}
        {showDays && <DateTimeDisplay small={small} medium={medium} value={days} type={"D"} />}
        {showHours && <DateTimeDisplay small={small} medium={medium} value={hours} type={"h"} />}
        {showMinutes && <DateTimeDisplay small={small} medium={medium} value={minutes} type={"m"} />}
        {showSeconds && <DateTimeDisplay small={small} medium={medium} value={seconds} type={"s"} />}
      </Timer>
    );
  }
};

const Timer = styled("div")`
  display: flex;
`;

const TimerItem = styled("div")`
  display: flex;
  align-items: baseline;
  margin: 0 6px 0 0;

  &:last-child {
    margin: 0;
  }

  p {
    margin: 0;
  }

    @media (max-width: 768px) {
    margin: 0 2px 0 0;
    
    &:last-child {
      margin: 0;
    }
  }
`;

export default CountdownTimer;
